import React from "react";
import { connect } from "react-redux";
import { userLogout, loginKeepAlive } from "actions/user";
import responseHandler from "responseHandler";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";
import WebConfig from "../../middlewares/WebConfig";
import { clockOut, clockIn } from "actions/account";

const MINUTES_UNITL_AUTO_LOGOUT = 30; // in mins
const MINUTES_WARNING_BEFORE_LOGOUT = 5;
const MINUTES_BETWEEN_KEEP_ALIVE = 5;
const CHECK_INTERVAL = 10000; // in ms
const STORE_LAST_ACTION = "STORE_LAST_ACTION";
const STORE_LAST_ACTION_WHEN_LAST_TOAST_CREATED =
  "STORE_LAST_ACTION_WHEN_LAST_TOAST_CREATED";
const STORE_LAST_TOAST = 'STORE_LAST_TOAST';
const STORE_TOAST_COUNT = 'STORE_TOAST_COUNT';

class AutoTimeout extends React.Component {
  constructor(props) {
    super(props);
    this.check();
    this.initListener();
    this.initInterval();
  }

  getLastAction() {
    return parseInt(sessionStorage.getItem(STORE_LAST_ACTION));
  }

  setLastAction(lastAction) {
    sessionStorage.setItem(STORE_LAST_ACTION, lastAction.toString());
  }

  getLastActionWhenLastToastCreated() {
    return parseInt(
      sessionStorage.getItem(STORE_LAST_ACTION_WHEN_LAST_TOAST_CREATED)
    );
  }

  setLastActionWhenLastToastCreated(lastActionWhenLastToastCreated) {
    sessionStorage.setItem(
      STORE_LAST_ACTION_WHEN_LAST_TOAST_CREATED,
      lastActionWhenLastToastCreated.toString()
    );
  }

  getLastToast() {
    return parseInt(
      sessionStorage.getItem(STORE_LAST_TOAST)
    );
  }

  setLastToast(lastToast) {
    sessionStorage.setItem(
      STORE_LAST_TOAST,
      lastToast.toString()
    );
  }

  getToastCount() {
    const toastCount = parseInt(sessionStorage.getItem(STORE_TOAST_COUNT));
    if (isNaN(toastCount)) {
      return 0;
    }
    return toastCount;
  }

  setToastCount(toastCount) {
    sessionStorage.setItem(
      STORE_TOAST_COUNT,
      toastCount.toString()
    );
  }

  initListener() {
    document.body.addEventListener("click", () => this.reset());
    document.body.addEventListener("mouseover", () => this.reset());
    document.body.addEventListener("mouseout", () => this.reset());
    document.body.addEventListener("keydown", () => this.reset());
    document.body.addEventListener("keyup", () => this.reset());
    document.body.addEventListener("keypress", () => this.reset());
  }

  reset() {
    this.setLastAction(Date.now());
    this.setLastActionWhenLastToastCreated('');
    this.setToastCount(0);
  }

  initInterval() {
    setInterval(() => {
      this.check();
    }, CHECK_INTERVAL);
    setInterval(() =>{
      this.checkLoginKeepAlive();
    }, MINUTES_BETWEEN_KEEP_ALIVE * 60 * 1000);
  }

  checkLoginKeepAlive(){
    const { userProfile, loginKeepAlive } = this.props;
    if (userProfile?.internetUserId){
      loginKeepAlive();
    }
  }

  check() {
    const { userProfile } = this.props;
    if (!isEmpty(userProfile)) {
      this.checkMidnight();
      this.checkTimeOut();
    }
  }

  checkMidnight() {
    const { activeClock } = this.props;
    let timeUntilMidnight = 1;
    let midnight = new Date();
    if(this.props.midnight) {
      midnight = new Date(this.props.midnight);
      timeUntilMidnight = (midnight.getTime() - new Date().getTime());
    }

    if (timeUntilMidnight <= 0) {
      if(activeClock && activeClock.clockInDate && !activeClock.clockOutDate) {
        if(new Date(activeClock.clockInDate) < midnight){
          let clockInTime = new Date(midnight); clockInTime.setSeconds(midnight.getSeconds() + 2);
          let clockOutTime = new Date(midnight); clockOutTime.setSeconds(midnight.getSeconds() - 2);
          this.props.clockOut(new Date().getTimezoneOffset(), clockOutTime.toISOString(), () => {
            this.props.clockIn(new Date().getTimezoneOffset(), clockInTime.toISOString());
          });
        }
      }
    }
  }

  checkTimeOut() {
    const { userLogout, makeToast, removeToast } = this.props;
    const now = Date.now();
    const timeOfLastAction = this.getLastAction();
    const timeOfLastActionWhenLastToastCreated = this.getLastActionWhenLastToastCreated();
    let toastCount = this.getToastCount();

    const timeleft = timeOfLastAction + MINUTES_UNITL_AUTO_LOGOUT * 60 * 1000;
    const diff = timeleft - now;

    let isTimeout = diff < 0;
    const isTimeoutWarning = diff < MINUTES_WARNING_BEFORE_LOGOUT * 60 * 1000;
    const textReason = "Auto logout due to inactivity";

    const minutesBeforeTimeout = Math.ceil(diff / 1000 / 60);
    const timeoutWarning = `${textReason} in ${minutesBeforeTimeout} minute${minutesBeforeTimeout !== 1 ? 's' : ''}`;
    if (isTimeoutWarning && minutesBeforeTimeout !== 0) {
      if (isNaN(timeOfLastActionWhenLastToastCreated)) {
        makeToast(timeoutWarning);
        this.setToastCount(1);
        this.setLastActionWhenLastToastCreated(timeOfLastAction);
        this.setLastToast(now);
      }
      else {
        const timeSinceLastToast = now - this.getLastToast();
        if (timeSinceLastToast >= 59999) {
          let childNodes = document.getElementById('snackbar-alert-dialog')?.childNodes;
          if(childNodes && childNodes[0]){
            childNodes[0].innerText = timeoutWarning;
          }
          this.setToastCount(toastCount + 1);
          this.setLastToast(now);
        }
      }
    }
    if (WebConfig.autoTimeoutDisabled) {
      return;
    }
    if (isTimeout) {
      sessionStorage.clear();
      removeToast();
      userLogout();
    }
  }

  render() {
    const { children } = this.props;
    return children;
  }
}

AutoTimeout.propTypes = {
  loginKeepAlive: PropTypes.func,
  userLogout: PropTypes.func,
  makeToast: PropTypes.func,
  removeToast: PropTypes.func,
  children: PropTypes.element,
  userProfile: PropTypes.object,
  activeClock: PropTypes.object,
  midnight: PropTypes.object,
  clockIn: PropTypes.func,
  clockOut: PropTypes.func,
};

const mapStateToProps = state => {
  return {
    userProfile: state.user.profile,
    activeClock: state.user.activeClock,
    midnight: state.user.midnight,
  };
};

const mapDispatchToProps = dispatch => ({
  userLogout() {
    dispatch(userLogout());
  },
  loginKeepAlive() {
    dispatch(loginKeepAlive());
  },
  makeToast(text) {
    responseHandler.customToast(text, "error", dispatch);
  },
  removeToast() {
    responseHandler.removeToast(dispatch);
  },
  clockOut(timeZoneOffset, clockInOutOverride, callback) {
    dispatch(clockOut(timeZoneOffset, clockInOutOverride, callback));
  },
  clockIn(timeZoneOffset, clockInOutOverride) {
    dispatch(clockIn(timeZoneOffset, clockInOutOverride));
  }
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AutoTimeout);
